/**
 * v0.4 追加7要件② (D-234 §1-2(b)): 土日祝の曜日色分けを1箇所に集約する共通コンポーネント。
 * `formatDateLabel` を使っていた各画面 (ShiftMyPage/ShiftBoardPage/ShiftPreferencesPage/
 * ShiftRecordsPage (旧ShiftAdjustmentsPage)/ShiftCoordinationPage/ShiftUrgentPage、
 * `withWeekday: true` の箇所) を置換する。
 * 設計: docs/design/v04-shift-additional-requirements.md §1-2(b)
 *
 * ⚠️ WCAG 1.4.1 (色だけで区別しない): 土日は曜日文字自体が既に区別の手段のため色は補助的な強調に
 * 留まるが、祝日は平日と同じ曜日文字になり色だけが唯一の差になる。そのため祝日には `•` (aria-hidden) +
 * 祝日名を `title`/スクリーンリーダー向けテキストとして併記する。
 * 色分けは `withWeekday` のときのみ適用する (曜日文字が無いと何の色か伝わらないため)。
 */
import { formatDateLabel } from '@/lib/format';
import { dayTextClass, holidayNameOf, resolveDayKind } from '@/lib/calendar-day';

export type DateLabelProps = {
  date: string;
  withWeekday?: boolean;
  withYear?: boolean;
  holidays?: ReadonlyMap<string, string> | null;
  className?: string;
};

export function DateLabel({ date, withWeekday = false, withYear = false, holidays, className }: DateLabelProps) {
  const label = formatDateLabel(date, { withWeekday, withYear });
  const holidayName = withWeekday ? holidayNameOf(date, holidays) : null;
  const colorClass = withWeekday ? dayTextClass(resolveDayKind(date, holidays)) : '';

  return (
    <span className={[colorClass, className].filter(Boolean).join(' ')} title={holidayName ?? undefined}>
      {label}
      {holidayName && (
        <>
          <span aria-hidden="true">・</span>
          <span className="sr-only">（{holidayName}）</span>
        </>
      )}
    </span>
  );
}
